import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/AddOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import UomFormDialog from './UomFormDialog';
import type { UomFormValues } from './UomFormDialog';
import type { CreateUnitOfMeasureRequest, UnitOfMeasureDto, UpdateUnitOfMeasureRequest } from '@/types/materials';

/** Units of Measure master — the source the Material master's Base UOM and Weight UOM pickers both
 * read from. Deliberately a real per-company master (not a hardcoded list), so a company can add
 * whatever units it actually trades in without a code change. */
export default function UomMaster() {
  const { t } = useTranslation('uomMaster');
  const queryClient = useQueryClient();

  const { data: uoms = [], isLoading } = useQuery({
    queryKey: ['units-of-measure'],
    queryFn: async () => (await apiClient.get<UnitOfMeasureDto[]>('/units-of-measure')).data,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUom, setEditingUom] = useState<UnitOfMeasureDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<UnitOfMeasureDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['units-of-measure'] });
    // The Materials list renders each material's unit off its own cached response, so a renamed or
    // deactivated unit has to bust that too.
    queryClient.invalidateQueries({ queryKey: ['materials'] });
  };

  // The API surfaces real rules as failures (duplicate code, unit still in use on delete) — show
  // them rather than letting the dialog close as if the save worked.
  const apiError = (e: unknown) =>
    (e as { response?: { data?: { errors?: string[] } } })?.response?.data?.errors?.[0] ?? t('errors.generic');

  const saveMutation = useMutation({
    mutationFn: (values: UomFormValues) => {
      if (editingUom) {
        const payload: UpdateUnitOfMeasureRequest = { code: values.code, name: values.name, status: values.status };
        return apiClient.put(`/units-of-measure/${editingUom.id}`, payload);
      }
      const payload: CreateUnitOfMeasureRequest = { code: values.code, name: values.name };
      return apiClient.post('/units-of-measure', payload);
    },
    onSuccess: () => {
      setError(null);
      invalidate();
      setDialogOpen(false);
      setEditingUom(null);
    },
    onError: (e) => setError(apiError(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/units-of-measure/${id}`),
    onSuccess: () => {
      setError(null);
      invalidate();
      setConfirmDelete(null);
    },
    onError: (e) => {
      setError(apiError(e));
      setConfirmDelete(null);
    },
  });

  const columns: DataTableColumn<UnitOfMeasureDto>[] = [
    { field: 'code', headerName: t('table.code') },
    { field: 'name', headerName: t('table.name') },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <IfPermitted permission={PERMISSIONS.MATERIAL_MANAGE}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => {
                setError(null);
                setEditingUom(r);
                setDialogOpen(true);
              }}
            >
              {t('edit')}
            </Button>
            <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
              {t('delete')}
            </Button>
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.MATERIAL_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setError(null);
                setEditingUom(null);
                setDialogOpen(true);
              }}
            >
              {t('addUom')}
            </Button>
          </IfPermitted>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <DataTable columns={columns} rows={uoms} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="units-of-measure" />

      <UomFormDialog
        open={dialogOpen}
        uom={editingUom}
        onClose={() => {
          setDialogOpen(false);
          setEditingUom(null);
        }}
        onSubmit={(values) => saveMutation.mutate(values)}
        loading={saveMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteUom.message')}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}
